import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomerList } from '../services/api';
import { DataTable, FormTextField, FormSelect } from '../shared';
import moment from 'moment';
import { Box, MenuItem, Link, Chip, Button, Stack } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  CardMembership as CardMembershipIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { THEME_COLORS, AiGradientButton } from '../shared';
import AddCustomerDrawer from './AddCustomerDrawer';
import './CustomerList.css';
import { formatHotelDate } from '../utils/dateUtils';


const CustomerList = () => {
  const navigate = useNavigate();
  const [customerData, setCustomerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    name: '',
    phone: '',
    city: '',
    state: '',
    is_active: '',
  });
  const [sortConfig, setSortConfig] = useState({ column: 'created_at', direction: 'desc' });
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 10,
    totalRecords: 0,
  });
  const [addDrawerOpen, setAddDrawerOpen] = useState(false);

  // Fetch customer data
  const fetchCustomerData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const sortColumn = sortConfig.column || 'created_at';
      const sortDirection = sortConfig.direction || 'desc';
      const sortParam = sortDirection === 'desc' ? `-${sortColumn}` : sortColumn;

      const params = {
        page: pagination.page + 1,
        per_page: pagination.perPage,
        sort: sortParam,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        ),
      };

      const response = await getCustomerList(params);
      
      if (response && response.Data) {
        setCustomerData(response.Data);
        setPagination(prev => ({
          ...prev,
          totalRecords: response.total_records || 0,
        }));
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch customer data');} finally {
      setLoading(false);
    }
  }, [filters, sortConfig, pagination.page, pagination.perPage]);

  useEffect(() => {
    fetchCustomerData();
  }, [fetchCustomerData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleSortChange = (newSortConfig) => {
    setSortConfig(newSortConfig);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePerPageChange = (newPerPage) => {
    setPagination(prev => ({ ...prev, perPage: newPerPage, page: 0 }));
  };

  const handleResetFilters = () => {
    setFilters({
      name: '',
      phone: '',
      city: '',
      state: '',
      is_active: '',
    });
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const columns = [
    {
      field: 'name',
      label: 'Name',
      sortable: true,
      render: (value, row) => (
        <Link
          component="button"
          variant="body2"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/customerhub/customers/${row.id}/`);
          }}
          sx={{
            textDecoration: 'none',
            fontWeight: 600,
            color: 'primary.main',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {value}
        </Link>
      ),
    },
    {
      field: 'phone',
      label: 'Phone',
      sortable: true,
      render: (value) => value && value !== '—' ? value : '—',
    },
    {
      field: 'location',
      label: 'Location',
      sortable: false,
    },
    {
      field: 'loyalty_member_no',
      label: 'Loyalty Number',
      sortable: false,
      render: (value) => {
        if (!value || value === '—') return '—';
        
        return (
          <Chip
            icon={<CardMembershipIcon sx={{ fontSize: 16, color: '#000 !important' }} />}
            label={value}
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: '#000',
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 28,
              borderRadius: '14px',
              boxShadow: '0 4px 12px rgba(217, 119, 6, 0.4)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.2s ease-in-out',
              '& .MuiChip-icon': {
                color: '#000',
                marginLeft: '8px',
              },
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(217, 119, 6, 0.6), 0 6px 20px rgba(0,0,0,0.15)',
              },
            }}
          />
        );
      },
    },
    {
      field: 'total_bookings',
      label: 'Total Bookings',
      sortable: true,
      align: 'right',
      render: (value) => <strong>{value || 0}</strong>,
    },
    {
      field: 'total_spent',
      label: 'Total Spent',
      sortable: true,
      align: 'right',
      render: (value, row) => {
        if (!value || value === 0) return '—';
        return (
          <strong>
            {row.currency} {parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </strong>
        );
      },
    },
    {
      field: 'last_booking',
      label: 'Last Booking',
      sortable: true,
      render: (value) => value && value !== '—' ? formatHotelDate(value) : '—',
    },
    {
      field: 'status',
      label: 'Status',
      sortable: true,
      render: (value, row) => {
        const isActive = row.is_active;
        const StatusIcon = isActive ? CheckCircleIcon : BlockIcon;
        
        return (
          <Chip
            icon={<StatusIcon sx={{ fontSize: 16, color: '#000 !important' }} />}
            label={value}
            size="small"
            sx={{
              background: isActive 
                ? 'linear-gradient(135deg, #34d399 0%, #10b981 100%)'
                : 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)',
              color: '#000',
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 28,
              borderRadius: '14px',
              boxShadow: isActive 
                ? '0 4px 12px rgba(16, 185, 129, 0.4)'
                : '0 4px 12px rgba(239, 68, 68, 0.4)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              textTransform: 'capitalize',
              transition: 'all 0.2s ease-in-out',
              '& .MuiChip-icon': {
                color: '#000',
                marginLeft: '8px',
              },
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: isActive 
                  ? '0 4px 12px rgba(16, 185, 129, 0.6), 0 6px 20px rgba(0,0,0,0.15)'
                  : '0 4px 12px rgba(239, 68, 68, 0.6), 0 6px 20px rgba(0,0,0,0.15)',
              },
            }}
          />
        );
      },
    },
  ];

  // Create filter row cells
  const filterRowCells = columns.map((column) => {
    switch (column.field) {
      case 'name':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Name"
            value={filters.name}
            onChange={(e) => handleFilterChange('name', e.target.value)}
            sx={{ minWidth: 120 }}
          />
        );
      case 'phone':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Phone"
            value={filters.phone}
            onChange={(e) => handleFilterChange('phone', e.target.value)}
            sx={{ minWidth: 120 }}
          />
        );
      case 'location':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Location"
            sx={{ minWidth: 120 }}
          />
        );
      case 'status':
        return (
          <FormSelect
            key={column.field}
            label=""
            value={filters.is_active}
            onChange={(e) => handleFilterChange('is_active', e.target.value)}
            displayEmpty
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </FormSelect>
        );
      default:
        return <div key={column.field}></div>;
    }
  });

  return (
    <Box className="customer-list-container">
      {error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
          {error}
        </Box>
      )}

      {/* DataTable */}
      <DataTable
          columns={columns}
          data={customerData}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
          onSortChange={handleSortChange}
          sortConfig={sortConfig}
          emptyMessage="No customers found matching the selected filters."
          stickyHeader={true}
          filterRow={filterRowCells}
          toolbarRight={
            <Stack direction="row" spacing={1} alignItems="center">
              <AiGradientButton onClick={() => navigate('/customerhub/retention/')}>
                AI Retention
              </AiGradientButton>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setAddDrawerOpen(true)}
                sx={{
                  backgroundColor: THEME_COLORS.PRIMARY,
                  color: '#000',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 1,
                  '&:hover': {
                    backgroundColor: THEME_COLORS.PRIMARY_HOVER || '#3dd4b0',
                    boxShadow: 2,
                  },
                }}
              >
                Add Customer
              </Button>
            </Stack>
          }
        />

        <AddCustomerDrawer
          open={addDrawerOpen}
          onClose={() => setAddDrawerOpen(false)}
          onSuccess={fetchCustomerData}
        />
    </Box>
  );
};

export default CustomerList;

